import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  Colour,
  FileTransferFeature,
  getPersonProfileLink,
  usePermissions,
  usePersonalSettings,
} from '@tyro/api';
import { DatePicker } from '@tyro/core/date-picker';
import { Dialog, DialogCloseButton, DialogContent } from '@tyro/core/dialog';
import { useDebouncedValue, usePreferredNameLayout } from '@tyro/core/hooks';
import { LoadingPlaceholder } from '@tyro/core/loading-placeholder';
import { mixHexColors } from '@tyro/core/utils';
import { useTranslation } from '@tyro/i18n';
import {
  CameraIcon,
  CollapseIcon,
  ExpandIcon,
  EyeSlashIcon,
  FolderDotIcon,
  MedicalCardPatientIcon,
  NotebookIcon,
} from '@tyro/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMeasure } from 'react-use';
import { useNotes } from '../../../api/note/list';
import { useStudentAenData } from '../../../api/student/aen/student-aen-data';
import { useStudentMedicalConditions } from '../../../api/student/medicals/student-medical-data';
import { useStudent } from '../../../api/student/students';
import { useStudentTimeline } from '../../../api/student/timeline';
import {
  type NeedToKnowGroup,
  useStudentActions,
} from '../../../store/student-actions-context';
import { Timeline, type TimelineProps } from '../../common/timeline/core';
import { StudentAvatarPicture } from '../student-avatar/student-avatar-picture';
import { NeedToKnowActions } from './actions';
import { NTKAen } from './aen';
import { NTKCondition } from './condition';
import { NTKContainer } from './container';
import { NTKNote } from './notes';
import { PanelTabs, type PanelTabsProps } from './panel-tabs';
import { PersonalInfoCard } from './personal-info';

type ResponseError = {
  response: Response;
} | null;

interface NeedToKnowModalProps {
  id: string;
  studentPartySettings:
    | { partyId: number; group?: NeedToKnowGroup }
    | undefined;
  open: boolean;
  onClose: () => void;
}

function NTKTabPanel({
  tabs,
  tabKey,
  currentTab,
  children,
}: {
  tabs: PanelTabsProps['tabs'];
  tabKey: PanelTabsProps['tabs'][number]['key'];
  currentTab: PanelTabsProps['tabs'][number]['key'];
  children: React.ReactNode;
}) {
  const matchedTab = tabs.find((tab) => tab.key === tabKey);

  if (!matchedTab) return null;

  return (
    <div
      role="tabpanel"
      id={matchedTab.panelId}
      aria-labelledby={matchedTab.id}
      hidden={currentTab !== matchedTab.key}
      style={{
        display: currentTab === matchedTab.key ? 'block' : 'none',
      }}
    >
      {children}
    </div>
  );
}

export function NeedToKnowModal({
  id,
  studentPartySettings,
  open,
  onClose,
}: NeedToKnowModalProps) {
  const { partyId: studentPartyId = 0, group } = studentPartySettings ?? {};
  const { t } = useTranslation(['common', 'people']);
  const { displayName } = usePreferredNameLayout();
  const { isStaffUserWithPermission } = usePermissions();
  const { palette, customShadows } = useTheme();
  const { security_dataPrivacy_confirmBeforeShowingSensitiveData } =
    usePersonalSettings();
  const [currentTab, setCurrentTab] =
    useState<PanelTabsProps['tabs'][number]['key']>('needToKnow');
  const [avatarRotation, setAvatarRotation] = useState(0);
  const [expandPhoto, setExpandPhoto] = useState(false);
  const [stackRef, { width }] = useMeasure<HTMLDivElement>();
  const {
    value: hideInfoBlocker,
    debouncedValue: debouncedHideInfoBlocker,
    setValue: setHideInfoBlocker,
  } = useDebouncedValue({
    defaultValue: !security_dataPrivacy_confirmBeforeShowingSensitiveData,
  });
  const resolvedHideInfoBlocker = hideInfoBlocker || debouncedHideInfoBlocker;
  const [timelineDate, setTimelineDate] = useState<Dayjs>(dayjs());

  const { openNeedToKnowForStudent, getAvatarUploadButtonProps } =
    useStudentActions();
  const {
    data: student,
    isLoading: isStudentLoading,
    isPending: isTimelinePending,
  } = useStudent(studentPartyId, open);
  const {
    data: priorityNotes = [],
    error: notesError,
    isLoading: isNotesLoading,
  } = useNotes(
    {
      partyIds: [studentPartyId],
      priority: true,
    },
    open,
  );
  const {
    data: aenData = [],
    error: aenError,
    isLoading: isAenLoading,
  } = useStudentAenData(
    { studentPartyIds: [studentPartyId], active: true },
    open,
  );

  const {
    data: medicalConditions = [],
    error: medicalError,
    isLoading: isMedicalLoading,
  } = useStudentMedicalConditions(studentPartyId, { enabled: open });

  const hasTimelineAccess = isStaffUserWithPermission(
    'ps:1:student_timeline:student_timeline',
  );
  const hasPersonalInfoAccess = isStaffUserWithPermission(
    'ps:1:people:view_student_personal_information',
  );
  const { data: timelineData, isLoading: isTimelineLoading } =
    useStudentTimeline(
      {
        studentPartyIds: [studentPartyId],
        fromDate: timelineDate.format('YYYY-MM-DD'),
        toDate: timelineDate.format('YYYY-MM-DD'),
      },
      {
        enabled: open && hasTimelineAccess,
      },
    );

  const timelineEvents = useMemo<TimelineProps['events']>(() => {
    if (!timelineData) return [];

    return timelineData.events.map((event) => ({
      id: event.timelineId!,
      headline: event.headline,
      date: event.startDate,
      startDateTime: event.startDatetime,
      endDateTime: event.endDatetime,
      details: event.details,
      meta: event.meta!,
      createdBy: event.createdBy,
    }));
  }, [timelineData]);

  const aenEntries = aenData?.[0]?.entries ?? [];

  const name = student?.person ? displayName(student.person) : '';
  const isLoading =
    isStudentLoading ||
    isNotesLoading ||
    isAenLoading ||
    isMedicalLoading ||
    isTimelinePending;
  const hasPriorityNotesAccess =
    (notesError as ResponseError)?.response?.status !== 403;
  const hasAenAccess = (aenError as ResponseError)?.response?.status !== 403;
  const hasMedicalAccess =
    (medicalError as ResponseError)?.response?.status !== 403;

  const tabs = useMemo(() => {
    const values: PanelTabsProps['tabs'] = [];

    if (hasPriorityNotesAccess || hasAenAccess || hasMedicalAccess) {
      values.push({
        key: 'needToKnow',
        id: `${id}-ntk-tab`,
        panelId: `${id}-ntk-panel`,
        name: t('people:needToKnow'),
      });
    }

    if (hasTimelineAccess) {
      values.push({
        key: 'timeline',
        id: `${id}-timeline-tab`,
        panelId: `${id}-timeline-panel`,
        name: t('people:timeline'),
      });
    }

    if (hasPersonalInfoAccess) {
      values.push({
        key: 'personalInfo',
        id: `${id}-personal-info-tab`,
        panelId: `${id}-personal-info-panel`,
        name: t('people:personalDetails'),
      });
    }

    return values;
  }, [
    hasPriorityNotesAccess,
    hasAenAccess,
    hasMedicalAccess,
    hasTimelineAccess,
    hasPersonalInfoAccess,
    id,
  ]);

  const reopenNeedToKnow = useCallback(() => {
    openNeedToKnowForStudent(studentPartyId, group);
  }, [studentPartyId, group]);

  const closeAndReset = useCallback(() => {
    onClose();
    setExpandPhoto(false);

    if (security_dataPrivacy_confirmBeforeShowingSensitiveData) {
      setHideInfoBlocker(false);
    }
  }, [
    onClose,
    security_dataPrivacy_confirmBeforeShowingSensitiveData,
    setHideInfoBlocker,
    setExpandPhoto,
  ]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 's') {
        setAvatarRotation((prev) => prev + 360);
      }
    };

    if (expandPhoto) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [expandPhoto]);

  useEffect(() => {
    const hasTab = tabs.some(({ key }) => key === currentTab);

    if (!hasTab && tabs[0]) {
      setCurrentTab(tabs[0].key as PanelTabsProps['tabs'][number]['key']);
    }
  }, [tabs]);

  useEffect(() => {
    if (studentPartyId === 0) {
      setCurrentTab(tabs[0].key as PanelTabsProps['tabs'][number]['key']);
    }
  }, [studentPartyId]);

  const canUploadStudentAvatar = isStaffUserWithPermission(
    'ps:1:documents:write_student_photos',
  );

  const studentLink = getPersonProfileLink(student?.person) ?? undefined;

  return (
    <Dialog
      id={id}
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={closeAndReset}
      className={resolvedHideInfoBlocker ? undefined : 'hide-details'}
      sx={{
        '& .MuiDialog-paper': {
          height: 'calc(100vh - 110px)',
          marginBottom: '95px',
        },

        '& .MuiTabs-root': {
          position: 'sticky',
          top: 0,
          zIndex: 2,
        },

        '& .actions': {
          display: 'flex',
          transition: 'opacity 0.15s ease-in-out, visibility 0.15s ease-in-out',
          opacity: 1,
          visibility: 'visible',
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          py: 0.75,
          px: 2,
          overflow: 'auto',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },

          '& > div': {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            mx: 'auto',
          },
        },

        '& .info-blocker': {
          transition: 'opacity 0.15s ease-in-out, visibility 0.15s ease-in-out',
          opacity: 0,
          visibility: 'hidden',
          position: 'absolute',
          zIndex: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          top: 0,
          left: 0,
          width: '100%',
          minHeight: '100%',
          p: 2,
          background: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(8px)',
          textAlign: 'center',

          '& svg': {
            width: 60,
            height: 60,
          },

          '& p': {
            maxWidth: 340,
          },
        },

        '&.hide-details': {
          '& .actions': {
            opacity: 0,
            visibility: 'hidden',
          },

          '& .info-blocker': {
            opacity: 1,
            visibility: 'visible',
          },
        },

        '@media (min-width: 700px)': {
          '& .MuiDialog-paper': {
            height: 'calc(100vh - 32px)',
            maxHeight: 920,
            marginBottom: 4,
          },

          '& .actions': {
            top: 0,
            left: 'auto',
            bottom: 'auto',
            transform: 'translate(min(444px, 100vw), 0px)',
            width: 'auto',
            height: '100vh',
            py: 3,

            '& > div': {
              flexDirection: 'column',
              my: 'auto',
            },
          },
        },

        '@media (max-height: 500px)': {
          '& .MuiTabs-root': {
            position: 'relative',
          },
        },
      }}
    >
      {isLoading ? (
        <LoadingPlaceholder sx={{ height: '100%' }} />
      ) : (
        <>
          <Stack
            ref={stackRef}
            className={expandPhoto ? 'expand-photo' : undefined}
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={3}
            sx={{
              position: 'absolute',
              height: 132,
              width: '100%',
              zIndex: 3,
              top: 0,
              p: 2,
              backgroundColor: mixHexColors(
                '#ffffff',
                palette.indigo[100],
                0.1,
              ),
              boxShadow: customShadows.z1,
              transition: 'height 0.15s ease-in-out',
              willChange: 'height',

              '&.expand-photo': {
                height: '100%',
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <IconButton
                aria-label={
                  expandPhoto ? t('common:collapse') : t('common:expand')
                }
                sx={{
                  padding: 0,
                  transformOrigin: 'top left',
                  transition: 'transform 0.15s ease-in-out',

                  '.expand-photo &': {
                    transform: `scale(${width / 100})`,
                  },

                  '& .MuiAvatar-root': {
                    transition: 'transform 0.3s ease-in-out',
                    transform: `rotate(${avatarRotation}deg)`,
                  },

                  '& .avatar-button-icon': {
                    position: 'absolute',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: alpha(palette.slate['800'], 0.4),
                    backdropFilter: 'blur(1px)',
                    zIndex: 1,
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    opacity: 0,
                    transition: 'opacity 0.15s ease-in-out',

                    '& svg': {
                      color: palette.common.white,
                      width: 32,
                      height: 32,
                    },
                  },
                  '&:hover, &:focus': {
                    backgroundColor: 'transparent',
                    '& .avatar-button-icon': {
                      opacity: 1,
                    },
                  },
                }}
                onClick={(e) => {
                  setExpandPhoto(!expandPhoto);
                  e.currentTarget.blur();
                }}
              >
                <div className="avatar-button-icon">
                  {expandPhoto ? <CollapseIcon /> : <ExpandIcon />}
                </div>
                <StudentAvatarPicture
                  name={name}
                  src={student?.person?.avatarUrl}
                  isPriorityStudent={!!student?.extensions?.priority}
                  hasSupportPlan={!!student?.extensions?.aen}
                  hasMedical={!!student?.extensions?.medical}
                  size={100}
                  person={student?.person}
                />
              </IconButton>
              <Stack
                sx={{
                  transition: 'opacity 0.15s ease-in-out',
                  '.expand-photo &': {
                    opacity: 0,
                  },
                }}
              >
                <Typography
                  component="h2"
                  variant="h4"
                  sx={{
                    '& > a': {
                      textDecoration: 'none',
                      cursor: 'pointer',
                      color: 'inherit',
                    },
                    '& > a:hover, > a:focus-visible': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <a href={studentLink} onClick={closeAndReset}>
                    {name}
                  </a>
                </Typography>
                <Typography variant="body1">
                  {student?.classGroup?.name}
                </Typography>
              </Stack>
              {canUploadStudentAvatar && (
                <Button
                  {...getAvatarUploadButtonProps(
                    {
                      partyId: studentPartyId,
                      uploadType: FileTransferFeature.StudentPhotos,
                    },
                    {
                      onClick: onClose,
                      onClose: reopenNeedToKnow,
                    },
                  )}
                  sx={{
                    opacity: 0,
                    visibility: 'hidden',
                    position: 'absolute',
                    transition:
                      'opacity 0.15s ease-in-out, visibility 0.15s ease-in-out',
                    top: width + 36,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    ml: '0 !important',

                    '.expand-photo &': {
                      opacity: 1,
                      visibility: 'visible',
                    },
                  }}
                  startIcon={<CameraIcon />}
                >
                  {t('common:changeAvatar')}
                </Button>
              )}
            </Stack>
            <DialogCloseButton
              onClick={closeAndReset}
              sx={{ mt: '6px !important' }}
            />
          </Stack>
          <DialogContent
            sx={{
              pt: 16.5,
              px: 2,
              pb: 2,
              backgroundColor: mixHexColors(palette.indigo[50], '#fff', 0.3),

              '& .MuiTabs-root': {
                pt: 1,
              },
            }}
          >
            <PanelTabs
              componentId={id}
              tabs={tabs}
              value={currentTab}
              setTab={setCurrentTab as (value: string) => void}
            />
            <NTKTabPanel
              tabs={tabs}
              tabKey="needToKnow"
              currentTab={currentTab}
            >
              {hasPriorityNotesAccess && (
                <NTKContainer
                  heading={t('common:notes')}
                  icon={<NotebookIcon />}
                  headerLink={`/people/students/${studentPartyId}/notes`}
                  hasItems={priorityNotes.length > 0}
                  noItemsText={t('people:noPriorityNotes')}
                  onClose={closeAndReset}
                >
                  {priorityNotes.map((note) => (
                    <NTKNote key={note.id} note={note} />
                  ))}
                </NTKContainer>
              )}
              {hasAenAccess && (
                <NTKContainer
                  heading="AEN"
                  icon={<FolderDotIcon />}
                  colour={Colour.Blue}
                  headerLink={`/people/students/${studentPartyId}/aen`}
                  hasItems={aenEntries.length > 0}
                  noItemsText={t('people:noAenRecords')}
                  onClose={closeAndReset}
                >
                  {aenEntries.map((aen) => (
                    <NTKAen key={aen.id} aen={aen} />
                  ))}
                </NTKContainer>
              )}
              {hasMedicalAccess && (
                <NTKContainer
                  heading={t('people:medicalConditions')}
                  icon={<MedicalCardPatientIcon />}
                  colour={Colour.Emerald}
                  headerLink={`/people/students/${studentPartyId}/medical`}
                  hasItems={medicalConditions.length > 0}
                  noItemsText={t('people:noMedicalRecords')}
                  onClose={closeAndReset}
                >
                  {medicalConditions.map((medicalCondition) => (
                    <NTKCondition
                      key={medicalCondition.id}
                      condition={medicalCondition}
                    />
                  ))}
                </NTKContainer>
              )}
            </NTKTabPanel>
            <NTKTabPanel tabs={tabs} tabKey="timeline" currentTab={currentTab}>
              <Stack mt={1.5} spacing={1}>
                <DatePicker
                  label={t('common:date')}
                  value={timelineDate}
                  onChange={(date) => {
                    if (date) {
                      setTimelineDate(date);
                    }
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                    },
                  }}
                  variant="white-filled-outlined"
                />
                <Timeline
                  events={timelineEvents}
                  showGroupLabels={false}
                  showActions={false}
                  loading={isTimelineLoading}
                  containerProps={{
                    sx: {
                      boxShadow: 'none',
                      backgroundColor: 'white',
                    },
                  }}
                  timelineContainerProps={{
                    sx: {
                      backgroundColor: 'white',
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 1,
                      minHeight: 200,
                      height: 'calc(100vh - 280px)',
                    },
                  }}
                />
              </Stack>
            </NTKTabPanel>
            <NTKTabPanel
              tabs={tabs}
              tabKey="personalInfo"
              currentTab={currentTab}
            >
              <PersonalInfoCard studentPartyId={studentPartyId} open={open} />
            </NTKTabPanel>
          </DialogContent>
          <NeedToKnowActions
            studentPartySettings={studentPartySettings}
            open={open}
            onActionClick={onClose}
            onActionNavigation={closeAndReset}
            onActionClose={reopenNeedToKnow}
          />
        </>
      )}
      <Stack className="info-blocker" spacing={3}>
        <EyeSlashIcon />
        <Stack spacing={1}>
          <Typography variant="subtitle1" component="h3">
            {t('common:sensitiveContent')}
          </Typography>
          <Typography variant="body2">
            {t('common:sensitiveInformationWarning')}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button variant="soft" color="inherit" onClick={closeAndReset}>
            {t('common:actions.back')}
          </Button>
          <Button variant="soft" onClick={() => setHideInfoBlocker(true)}>
            {t('common:actions.confirm')}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}
